<script lang="ts">
import { defineComponent } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";
import { DataModules } from "@/store/modules/data/modules";
import { ProductTypes, RelatedProductsTypes } from "@/data/enums/products";
import { PriceDisplayTypes } from "@/data/enums/prices";
import { BrandConfigKeys, QUERY_PARAMS } from "@/data/constants";
import { getProductRoute } from "@/data/shop/utils";
import { RegexMatch } from "@/data/enums/router";
import type { PropType, CreateElement } from "vue";
import type {
  IStoreProviderData,
  IStoreProviderMethods
} from "@/models/providers/store";
import type { IBasketProviderData } from "@/models/providers/baskets";
import type { IProvisionProviderData } from "@/models/providers/provisioning";
import type { IShopProviderMethods } from "@/models/providers/shop";
import type { IProductCategory, IProductPrice } from "@/models/products";
import type { IState } from "@/store";

export default defineComponent({
  name: "StoreProvider",
  mixins: [RequestPromises, Context],
  provide() {
    return {
      storeProviderData: this.storeProviderData,
      storeProviderMethods: this.storeProviderMethods
    };
  },
  inject: {
    $basketProviderData: {
      from: "basketProviderData",
      type: Function as PropType<IBasketProviderData>
    },
    $provisionProviderData: {
      from: "provisionProviderData",
      type: Function as PropType<IProvisionProviderData>
    },
    $shopMethods: {
      from: "shopProviderMethods",
      type: Object as PropType<IShopProviderMethods>
    }
  },
  props: {
    handleLoading: { type: Boolean, default: true },
    loaderAttrs: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({})
    },
    handleRequestError: { type: Boolean, default: true }
  },
  data: () => ({
    isLoading: false,
    isReloading: false,
    hasRequestError: false,
    productsAreLoading: false,
    productsAreLoadingMore: false,
    productsAreReloading: false,
    productsAreFiltering: false,
    productsRequestError: false,
    offset: 0,
    limit: 12,
    hasMore: false,
    searchValue: "",
    productTypes: [ProductTypes.SINGLE_PRODUCT, ProductTypes.PRODUCT_BUNDLE],
    productsCache: {} as object,
    categoriesAreFiltered: false
  }),
  computed: {
    ...mapState({
      rootCategories(state: IState, getters): IProductCategory[] {
        return (
          getters[`data/listArray`]({
            storeModule: DataModules.CATALOGUE_CATEGORIES,
            scope: this.rootCategoriesScope
          }) || []
        );
      },
      rootCategoriesScope(state: IState, getters): string {
        return getters[
          `data/${DataModules.CATALOGUE_CATEGORIES}/rootCategoriesScope`
        ](this.basketData.basketBrandId, this.productTypes);
      }
    }),
    basketData() {
      return this.$basketProviderData();
    },
    provisionData() {
      return this.$provisionProviderData();
    },
    pathCategoryId() {
      return _.get(this.$route.query, QUERY_PARAMS.CATEGORY_ID, "") as string;
    },
    selectedCategory() {
      return this.getCategory(this.rootCategories, this.pathCategoryId);
    },
    selectedCategoryProducts() {
      return this.getCategoryProductsFromCache(this.pathCategoryId);
    },
    hasDacWidget() {
      return _.get(this.selectedCategory, "meta.widgets.dac");
    },
    storeProviderMethods(): IStoreProviderMethods {
      return {
        addCategoryToQuery: this.addCategoryToQuery,
        filterCategories: this.filterCategories,
        getCategory: this.getCategory,
        getRootCategories: this.getRootCategories,
        configureProduct: this.configureProduct,
        loadCategoryProducts: this.loadCategoryProducts,
        searchByName: this.searchByName,
        setInitialCategory: this.setInitialCategory,
        loadMore: this.loadMore,
        reloadData: this.reloadData
      };
    }
  },
  watch: {
    pathCategoryId: { handler: "loadCategoryProducts", immediate: true }
  },
  async created() {
    this.isLoading = true;
    await this.loadData();
  },
  beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CATALOGUE_CATEGORIES,
      scope: this.rootCategoriesScope,
      vm: this
    });
  },
  methods: {
    async loadData() {
      try {
        await this.getRootCategories(this.isReloading);
        this.setInitialCategory();
        this.hasRequestError = false;
      } catch (error) {
        if (this.$store.getters["api/is409"](error) && !this.isAdmin) return;
        this.hasRequestError = true;
        this.$store.dispatch("api/handleError", error);
      } finally {
        this.isLoading = false;
        this.isReloading = false;
      }
    },
    reloadData() {
      this.isReloading = true;
      return this.loadData();
    },
    getRootCategories(ignoreStored = false) {
      return this.$store.dispatch(
        `data/${DataModules.CATALOGUE_CATEGORIES}/list`,
        {
          isBasketContext: true,
          productTypes: this.productTypes,
          scope: this.rootCategoriesScope,
          params: _.merge(
            this.basketData.accountId
              ? {
                  account_id: this.basketData.accountId
                }
              : {},
            {
              currency_id: this.basketData.currencyId,
              order: "order",
              limit: 0,
              "filter[level]": 1,
              with_count: [
                "products",
                "subcategories.products",
                "subcategories.subcategories.products",
                "subcategories.subcategories.subcategories.products",
                "subcategories.subcategories.subcategories.subcategories.products"
              ].join(),
              with: [
                "subcategories.image",
                "subcategories.subcategories.image",
                "subcategories.subcategories.subcategories.image",
                "subcategories.subcategories.subcategories.subcategories.image"
              ].join()
            }
          ),
          vm: this,
          ignoreStored
        }
      );
    },
    async loadMore() {
      this.productsAreLoadingMore = true;
      try {
        await this.getCategoryProducts(this.pathCategoryId);
        this.productsRequestError = false;
      } catch (error) {
        this.$store.dispatch("api/handleError", error);
        this.productsRequestError = true;
      } finally {
        this.productsAreLoadingMore = false;
      }
    },
    async searchByName(value: string) {
      this.searchValue = value;

      this.offset = 0;
      this.hasMore = false;
      this.$set(this.productsCache, this.pathCategoryId, {});

      try {
        if (!this.productsAreFiltering) {
          this.productsAreFiltering = true;
        }
        await this.getCategoryProducts(this.pathCategoryId);
        this.productsRequestError = false;
      } catch (error) {
        this.productsRequestError = true;
        this.$store.dispatch("api/handleError", error);
      }
      this.productsAreFiltering = false;
    },
    updateRouteSearchQuery(categoryId: IProductCategory["id"]) {
      let query = _.cloneDeep(this.$route.query);

      if (this.searchValue.length) {
        query = {
          ...query,
          [QUERY_PARAMS.SEARCH]: _.get(this.getParams(categoryId), "search")
        };
      } else {
        delete query[QUERY_PARAMS.SEARCH];
      }

      this.$router.replace({ query }).catch(err => err);
    },
    async getCategoryProducts(categoryId: IProductCategory["id"]) {
      try {
        this.updateRouteSearchQuery(categoryId);
        const searchedValue = this.$_.clone(this.searchValue);
        const results = await this.$store.dispatch(
          `data/${DataModules.CATALOGUE}/list`,
          {
            clientApi: true,
            isBasketContext: true,
            params: this.getParams(categoryId),
            returnData: false,
            storeData: false,
            vm: this
          }
        );

        if (
          this.pathCategoryId !== categoryId ||
          searchedValue !== this.searchValue
        )
          return Promise.resolve();

        this.offset = this.offset + this.limit;
        this.hasMore = results.total > this.offset;

        this.$set(this.productsCache, categoryId, {
          products: _.concat(
            [...this.getCategoryProductsFromCache(categoryId)],
            this.mapToProducts(_.get(results, "data", []))
          ),
          total: results.total
        });
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getParams(categoryId: IProductCategory["id"]) {
      const basket_id = this.basketData.basketId;
      const params: any = _.merge(
        {
          brand_id: this.basketData.basketBrandId,
          currency_id: this.basketData.currencyId,
          promotions: this.basketData.coupons.join(),
          limit: this.limit,
          order: "order",
          offset: this.offset,
          ...(basket_id && basket_id !== RegexMatch.NEW ? { basket_id } : {}),
          with: "image,prices,attributes,options,options.prices,related"
        },
        this.searchValue.length
          ? { search: `${this.searchValue}` }
          : { "filter[products_category_id]": categoryId }
      );

      if (!_.isEmpty(_.get(this.basketData, "account", {}))) {
        params.pricelist_id = _.get(
          this.basketData,
          "account.pricelist_id",
          null
        );
        params.account_id = _.get(this.basketData, "accountId", null);
      }

      return params;
    },
    mapToProducts(products) {
      return _.map(products, product => {
        const asLMFP = // asLMFP = as Lowest Monthly From Price
          this.$store.getters["brand/config"][
            BrandConfigKeys.PRICE_DISPLAY_TYPE
          ] === PriceDisplayTypes.MONTHLY_FROM;

        // Map prices
        product.prices = _.orderBy(
          _.map(product.prices, price => ({
            ..._.pick(price as IProductPrice, [
              "billing_cycle_months",
              "mixed_promotions",
              "monthly_price_from",
              "monthly_price_from_discounted_formatted",
              "monthly_price_from_formatted",
              "price",
              "price_discounted",
              "price_discounted_formatted",
              "price_formatted"
            ]),
            ...this.$store.getters["data/catalogue/productTermSavings"](price),
            promotionsByName: _.map(
              price.promotions || [],
              promo => promo.name_translated || promo.name
            )
          })),
          [
            term => {
              return asLMFP
                ? _.isNil(term.price_discounted)
                  ? (term.monthly_price_from as number)
                  : (term.price_discounted as number) /
                    (term.billing_cycle_months as number)
                : term.billing_cycle_months;
            }
          ],
          ["asc"]
        );

        const term = _.first(product.prices) as IProductPrice;

        return {
          id: product.id,
          object_id: product.id,
          product: product,
          type: RelatedProductsTypes.PRODUCT,
          image: product.image,
          name: product.name_translated || product.name,
          hasMixedPromotions: _.some(product.prices, [
            "mixed_promotions",
            true
          ]),
          promotionsByName: _.uniq(
            _.flatten(_.map(product.prices, price => price.promotionsByName))
          ),
          description:
            product.short_description_translated ||
            product.short_description ||
            product.description_translated ||
            product.description,
          route: getProductRoute(this, {
            [QUERY_PARAMS.PRODUCT_ID]: product.id
          }),
          ..._.pick(term, ["saving", "saving_formatted", "saving_summary"]),
          fromPrice: this.$store.getters["data/catalogue/productTermFromPrice"](
            term,
            asLMFP
          ),
          oldFromPrice: this.$store.getters["utils/money/trimTrailingZeros"](
            asLMFP
              ? _.get(term, "monthly_price_from_formatted")
              : _.get(term, "price_formatted")
          ),
          unit_quantity: product.unit_quantity
        };
      });
    },
    setInitialCategory() {
      if (
        (!this.pathCategoryId || !this.selectedCategory) &&
        this.rootCategories.length !== 0
      ) {
        this.addCategoryToQuery(_.get(this.rootCategories, "[0].id"));
      }
    },
    filterCategories(areFiltered: boolean) {
      this.categoriesAreFiltered = areFiltered;
    },
    getCategory(categories, categoryId) {
      let selectedCategory;

      categories.some(function iter(category) {
        if (category.id === categoryId) {
          selectedCategory = category;
          return true;
        }
        return (
          Array.isArray(category.subcategories) &&
          category.subcategories.some(iter)
        );
      });

      return selectedCategory;
    },
    getCategoryProductsFromCache(categoryId) {
      return _.get(_.get(this.productsCache, categoryId), "products", []);
    },
    clearSearchValue(oldCategoryId) {
      this.searchValue = "";
      delete this.productsCache[oldCategoryId];

      this.updateRouteSearchQuery(this.pathCategoryId);
    },
    addCategoryToQuery(categoryId: IProductCategory["id"]) {
      const hasCategoryId = _.has(
        this.$router.currentRoute,
        `query.${QUERY_PARAMS.CATEGORY_ID}`
      );
      this.$router[hasCategoryId ? "push" : "replace"]({
        query: _.merge({}, this.$route.query, {
          [QUERY_PARAMS.CATEGORY_ID]: categoryId
        })
      }).catch(err => err);
    },
    getQuery() {
      const query = _.clone(this.$route.query);
      const regex = new RegExp(
        `^(${[
          QUERY_PARAMS.ACCOUNT_ID,
          QUERY_PARAMS.CLIENT_ID,
          QUERY_PARAMS.PRODUCT_ID,
          QUERY_PARAMS.BASKET_PRODUCT_ID,
          QUERY_PARAMS.BILLING_CYCLE_MONTHS,
          QUERY_PARAMS.QUANTITY
        ].join("|")})$`,
        "gm"
      );
      const matches = _.keys(query).join("\n").match(regex);
      if (!matches || !matches.length) return;
      matches.forEach(key => delete query[key]);
      return query;
    },
    configureProduct(queryParams: object) {
      const currentQueryParams = this.$route.query;
      delete currentQueryParams["upm-dac-search"];
      this.$shopMethods.goToProductView({
        queryParams: _.merge({}, currentQueryParams, queryParams)
      });
    },
    async loadCategoryProducts(categoryId, oldCategoryId) {
      if (this.searchValue.length && oldCategoryId) {
        this.clearSearchValue(oldCategoryId);
      }

      const querySearchValue = _.get(
        this.$route.query,
        QUERY_PARAMS.SEARCH
      ) as string;

      if (querySearchValue) {
        this.searchValue = querySearchValue;
      } else if (!categoryId) return this.setInitialCategory();

      this.offset = _.get(
        _.get(this.productsCache, categoryId),
        "products",
        []
      ).length;

      if (_.get(this.productsCache, categoryId)) {
        this.hasMore =
          _.get(this.productsCache, `${categoryId}.total`) > this.offset;
        return;
      }

      try {
        if (!this.productsAreReloading) {
          this.productsAreLoading = true;
        }
        await this.getCategoryProducts(categoryId);
        this.productsRequestError = false;
      } catch (error) {
        this.productsRequestError = true;
        this.$store.dispatch("api/handleError", error);
      }
      this.productsAreLoading = false;
      this.productsAreReloading = false;
    },
    storeProviderData(): IStoreProviderData {
      return {
        categoriesAreFiltered: this.categoriesAreFiltered,
        hasDacWidget: this.hasDacWidget,
        hasMore: this.hasMore,
        hasRequestError: this.hasRequestError,
        isLoading: this.isLoading,
        isReloading: this.isReloading,
        pathCategoryId: this.pathCategoryId,
        products: this.selectedCategoryProducts,
        productsAreFiltering: this.productsAreFiltering,
        productsAreLoading: this.productsAreLoading,
        productsAreLoadingMore: this.productsAreLoadingMore,
        productsAreReloading: this.productsAreReloading,
        productsRequestError: this.productsRequestError,
        rootCategories: this.rootCategories,
        searchValue: this.searchValue,
        selectedCategory: this.selectedCategory
      };
    }
  },
  render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", {
        props: { if: true },
        attrs: this.loaderAttrs
      });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $storeProviderData: this.storeProviderData(),
        $storeProviderMethods: this.storeProviderMethods
      })
    );
  }
});
</script>
